import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../../redux/actions/growthEngine';
import "flag-icons/css/flag-icons.min.css";
import { toast } from 'react-toastify';
import YouTubeIcon from '../../../assets/images/admin/Youtube.png';
import StarIcon from '../../../assets/images/admin/Star.svg';
import FacebookIcon from '../../../assets/images/icons/facebook-icon.png';
import InstagramIcon from '../../../assets/images/admin/Instagram.png';
import AdClicksIcon from '../../../assets/images/admin/campaign-editor/ad-clicks.png';
import ConversionsIcon from '../../../assets/images/admin/campaign-editor/conversions.png';
import TotalSpentIcon from '../../../assets/images/admin/campaign-editor/total-spent.png';
import ReachIcon from '../../../assets/images/admin/campaign-editor/reach.png';
import LikesReactionsIcon from '../../../assets/images/admin/campaign-editor/likes-reactions.png';
import AdLinkClicksIcon from '../../../assets/images/admin/campaign-editor/ad-link-clicks.png';
import AdCommentsIcon from '../../../assets/images/admin/campaign-editor/ad-comments.png';
import AdSharesIcon from '../../../assets/images/admin/campaign-editor/ad-shares.png';
import ImpressionsIcon from '../../../assets/images/admin/campaign-editor/impressions.png';
import CpmIcon from '../../../assets/images/admin/campaign-editor/cpm.png';
import AmplitudeIcon from '../../../assets/images/admin/amplitude-icon.png'
import { getGoalIconAndName, formatCampaignDateTime, getTimezoneLabel } from '../../../helper/utility';
import { getCampaignObjectiveLabel, getCampaignTargetLabel } from '../../../helper/campaignDisplay';
import { getCampaignConversionSettings, getCampaignConversionMetrics, formatConversionMetric, formatCostPerConversion, shouldShowFeatureFmBreakdown, getFeatureFmDestinationBreakdown, normalizeFeatureFmDestinationKey, getFeatureFmBreakdownCopy, shouldGateFeatureFmBreakdown, isMetaInsightsAvailable, formatInsightMetric, formatInsightCurrency, CONVERSION_EVENTS } from '../../../helper/campaignConversion';
import { canUseFeatureFmCampaignInsights, getUserDisplayName } from '../../../helper/access';
import { STREAM_ICONS, STREAM_UI } from '../LinkAndFanHub/constants/allPlatformIcon';
import ExternalLinkIcon from '../../../assets/images/icons/external-link.svg';
import { CampaignThumbnail } from '../../../components/CampaignThumbnail';
import { ObjectiveIcon } from './ObjectiveIcon';
import { EditGeographyModal } from './GeographyTargetModal';
import { EditCampaignModal } from './EditCampaignModal';
import { DuplicateCampaignModal } from './DuplicateCampaignModal';
import { MetaAdPreview, createCreativeMediaRenderer } from './MetaAdPreview';
import ChatInput from '../AmplitudeAI/Chat/ChatInput';
import { sendMessage } from '../../../redux/actions';
import { showToast } from '../../../helper/ShowToast';
import { getCampaignStatusBadge } from '../../../helper/campaignDisplayStatus';
import { PricingPlanModal } from '../../../components/PricingPlanModal';
import { CreditPlanModal } from '../../../components/CreditPlanModal';
import { useMetaCampaignDetails } from '../../../queries/growthEngine/metaCampaign.query';
import { useMetaCampaignList } from '../../../queries/growthEngine/metaCampaign.query';
import { useGrowthEngineActivityTracker } from '../../../hooks/useGrowthEngineActivityTracker';
import ArtistManagerDspLockOverlay from '../ArtistManager/ArtistManagerDspLockOverlay';

const GATED_STREAMING_DESTINATIONS = [
    { key: 'spotify', name: 'Spotify', clicks: 128, percent: 42 },
    { key: 'apple', name: 'Apple Music', clicks: 86, percent: 28 },
    { key: 'youtube', name: 'YouTube', clicks: 54, percent: 18 },
    { key: 'amazon', name: 'Amazon Music', clicks: 36, percent: 12 },
];

const GATED_BIO_DESTINATIONS = [
    { key: 'spotify', name: 'Spotify', clicks: 94, percent: 38 },
    { key: 'instagram', name: 'Instagram', clicks: 72, percent: 29 },
    { key: 'youtube', name: 'YouTube', clicks: 48, percent: 19 },
    { key: 'merchandise', name: 'Merchandise', clicks: 34, percent: 14 },
];

const GATED_TICKET_DESTINATIONS = [
    { key: 'ticketmaster', name: 'Ticketmaster', clicks: 64, percent: 58, isTicket: true },
    { key: 'eventbrite', name: 'Eventbrite', clicks: 46, percent: 42, isTicket: true },
];

const GATED_ENGAGEMENT_DESTINATIONS = [
    { key: 'spotify', name: 'Spotify', clicks: 38, percent: 55 },
    { key: 'instagram', name: 'Instagram', clicks: 31, percent: 45 },
];


export const CampaignEditor = () => {
    const max = 43.0;
    const spent = 33.53;
    const percentage = Math.min((spent / max) * 100, 100).toFixed(2);
    const navigate = useNavigate()
    const { campaignId } = useParams();
    const dispatch = useDispatch();
    const { goalOptions, duplicatedCamapignDetails } = useSelector(state => state.growthEngine);
    const { showLoad } = useSelector((state) => state?.amplitudeReducer);
    const { artistInfo } = useSelector(state => state?.artistDetails);
    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);
    const access = useSelector((state) => state.auth.access);
    const authToken = useSelector((state) => state.auth.token);
    const userDetails = useSelector((state) => state.auth.userDetails);
    const displayName = useMemo(
        () => getUserDisplayName(userDetails, artistInfo, access),
        [userDetails, artistInfo, access]
    );
    const [showEditGeographyModal, setShowEditGeographyModal] = useState(false);
    const closeEditGeographyModal = () => setShowEditGeographyModal(false);
    const openEditGeographyModal = () => setShowEditGeographyModal(true);
    const [showBudgetModal, setShowBudgetModal] = useState(false);
    const [openFor, setOpenFor] = useState('');
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [adViewAs, setAdViewAs] = useState('instaReels');
    const [activeCreativeIndex, setActiveCreativeIndex] = useState(0);

    const metaAccountKey =
        userDetails?.email || userDetails?.assignedArtistSongstateId || authToken || null;
    const activeArtistProfileId = access?.artist?.activeArtistProfileId || null;
    const metaCampaignUserKey = metaAccountKey
        ? `${metaAccountKey}:${activeArtistProfileId || "workspace"}`
        : null;

    const {
        data: campaignDetails,
        isLoading: loading,
    } = useMetaCampaignDetails({
        userKey: metaCampaignUserKey,
        campaignId,
        enabled: !!authToken && !!metaCampaignUserKey && !!campaignId,
    });

    const { data: metaCampaignListData } = useMetaCampaignList({
        userKey: metaCampaignUserKey,
        page: 1,
        limit: 50,
        enabled: !!authToken && !!metaCampaignUserKey,
    });

    const firstCampaignMetaId = [...(metaCampaignListData?.campaigns || [])].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    )[0]?.metaCampaignId || null;

    const isFirstLaunchedCampaign = !!firstCampaignMetaId && firstCampaignMetaId === campaignId;

    const { trackCampaignEditorVisited } = useGrowthEngineActivityTracker();
    const campaignEditorVisitTrackedRef = useRef(false);

    // Reset tracking ref when the viewed campaign changes so the new campaign
    // can fire its own visit event (prevents stale state across route changes).
    useEffect(() => {
        campaignEditorVisitTrackedRef.current = false;
    }, [campaignId]);

    // Raw Campaign Editor visit event. The backend only derives the
    // "review_analytics" reward from this when isFirstLaunchedCampaign and
    // hasEnded are both true (verified again server-side).
    useEffect(() => {
        if (!campaignDetails?.campaign) return;
        // Wait for the campaign list to load so isFirstLaunchedCampaign is accurate
        // before firing — otherwise firstCampaignMetaId would be null on first render.
        if (!metaCampaignListData) return;

        const stopTime = campaignDetails?.campaign?.stop_time;
        const hasEnded = campaignDetails?.campaign?.hasEnded ?? (!!stopTime && new Date(stopTime) < new Date());

        // Fire once for non-ended campaigns, and fire (or re-fire) once when
        // the campaign is detected as ended. This handles the case where the
        // page was first viewed while the campaign was still running.
        if (!hasEnded && campaignEditorVisitTrackedRef.current) return;
        if (hasEnded && campaignEditorVisitTrackedRef.current === 'ended') return;

        campaignEditorVisitTrackedRef.current = hasEnded ? 'ended' : 'not-ended';
        trackCampaignEditorVisited({ campaignId, isFirstLaunchedCampaign, hasEnded });
    }, [campaignDetails, campaignId, isFirstLaunchedCampaign, metaCampaignListData, trackCampaignEditorVisited]);

    const creatives = campaignDetails?.creativeDetails || [];
    const activeCreative = creatives[activeCreativeIndex] || creatives[0];

    useEffect(() => {
        setActiveCreativeIndex(0);
    }, [campaignId, creatives.length]);

    const facebookProfile = useMemo(() => ({
        name: campaignDetails?.campaign?.pageInfo?.name || 'Facebook Page',
        image: campaignDetails?.campaign?.pageInfo?.picture?.data?.url || '',
    }), [campaignDetails?.campaign?.pageInfo]);

    const instagramProfile = useMemo(() => ({
        name: campaignDetails?.campaign?.instagramInfo?.username || 'Instagram',
        image: campaignDetails?.campaign?.instagramInfo?.profile_picture_url || '',
    }), [campaignDetails?.campaign?.instagramInfo]);

    const renderMedia = useMemo(
        () => createCreativeMediaRenderer(activeCreative ? [activeCreative] : []),
        [activeCreative]
    );

    const getFeatureFmDestinationDisplay = (destination) => {
        const key = destination.key || normalizeFeatureFmDestinationKey(destination.name);
        const compactKey = key.replace(/_/g, '');
        const ui = STREAM_UI[key] || STREAM_UI[compactKey] || STREAM_UI[key.replace(/_music$/, '')];
        const icon = ui?.icon
            || STREAM_ICONS[key]
            || STREAM_ICONS[compactKey]
            || STREAM_ICONS[key.replace(/_music$/, '')]
            || ExternalLinkIcon;
        const label = ui?.label || destination.name
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (letter) => letter.toUpperCase())
            || 'Destination';

        return { icon, label };
    };
    const hasFeatureFmAccess = canUseFeatureFmCampaignInsights(
        access,
        userDetails,
        { hasActiveSubscription }
    );
    const conversionSettings = useMemo(
        () => getCampaignConversionSettings({
            campaign: campaignDetails?.campaign,
            destinationUrl: campaignDetails?.creativeDetails?.[0]?.destinationUrl,
            linkType: campaignDetails?.smartLinkAnalytics?.linkType,
        }),
        [campaignDetails?.campaign, campaignDetails?.creativeDetails, campaignDetails?.smartLinkAnalytics?.linkType]
    );
    const featureFmDestinations = useMemo(
        () => getFeatureFmDestinationBreakdown(campaignDetails?.smartLinkAnalytics, conversionSettings.conversionEvent),
        [campaignDetails?.smartLinkAnalytics, conversionSettings.conversionEvent]
    );
    const showFeatureFmBreakdown = shouldShowFeatureFmBreakdown({
        campaign: campaignDetails?.campaign,
        destinationUrl: campaignDetails?.creativeDetails?.[0]?.destinationUrl,
        smartLinkAnalytics: campaignDetails?.smartLinkAnalytics,
        conversionEvent: conversionSettings.conversionEvent,
        hasFeatureFmAccess,
    });
    const showGatedFeatureFmBreakdown = shouldGateFeatureFmBreakdown({
        campaign: campaignDetails?.campaign,
        destinationUrl: campaignDetails?.creativeDetails?.[0]?.destinationUrl,
        hasFeatureFmAccess,
    });
    const goalMatch = getGoalIconAndName(campaignDetails?.campaign?.name, goalOptions);
    const objectiveLabel = getCampaignObjectiveLabel(campaignDetails?.campaign, goalMatch);
    const featureFmBreakdownCopy = getFeatureFmBreakdownCopy(conversionSettings.conversionEvent);
    const isEventBreakdown = conversionSettings.conversionEvent === CONVERSION_EVENTS.TICKET;
    const ticketDestinations = featureFmDestinations.filter((destination) => destination.isTicket);
    const engagementDestinations = featureFmDestinations.filter((destination) => !destination.isTicket);
    const gatedDestinationPreview = useMemo(() => {
        if (conversionSettings.conversionEvent === CONVERSION_EVENTS.TICKET) {
            return {
                kind: 'event',
                tickets: GATED_TICKET_DESTINATIONS,
                engagement: GATED_ENGAGEMENT_DESTINATIONS,
            };
        }
        if (conversionSettings.conversionEvent === CONVERSION_EVENTS.BIO_LINK) {
            return { kind: 'list', destinations: GATED_BIO_DESTINATIONS };
        }
        return { kind: 'list', destinations: GATED_STREAMING_DESTINATIONS };
    }, [conversionSettings.conversionEvent]);
    const conversionMetrics = useMemo(
        () => getCampaignConversionMetrics({
            campaign: campaignDetails?.campaign,
            destinationUrl: campaignDetails?.creativeDetails?.[0]?.destinationUrl,
            smartLinkAnalytics: campaignDetails?.smartLinkAnalytics,
            actions: campaignDetails?.actions,
            insights: campaignDetails?.insights,
            hasFeatureFmAccess,
        }),
        [
            campaignDetails?.campaign,
            campaignDetails?.creativeDetails,
            campaignDetails?.smartLinkAnalytics,
            campaignDetails?.actions,
            campaignDetails?.insights,
            hasFeatureFmAccess,
        ]
    );
    const statusMap = {
        PAUSED: {
            label: 'Paused',
            className: 'bg-gray-400 text-gray-800'
        },
        ACTIVE: {
            label: 'Live',
            className: 'bg-green-400 text-green-800'
        },
        IN_REVIEW: {
            label: 'In Review',
            className: 'bg-yellow-400 text-yellow-900'
        },
        ARCHIVED: {
            label: 'Finished',
            className: 'bg-red-600 text-white'
        },
        DELETED: {
            label: 'Deleted',
            className: 'bg-red-500 text-white'
        },
        PENDING_REVIEW: {
            label: 'Pending Review',
            className: 'bg-yellow-300 text-yellow-900'
        },
        DISAPPROVED: {
            label: 'Disapproved',
            className: 'bg-red-500 text-white'
        },
        PREAPPROVED: {
            label: 'Preapproved',
            className: 'bg-blue-300 text-blue-900'
        },
        IN_PROCESS: {
            label: 'Processing',
            className: 'bg-blue-400 text-white'
        },
        WITH_ISSUES: {
            label: 'With Issues',
            className: 'bg-orange-400 text-white'
        },
        CAMPAIGN_PAUSED: {
            label: 'Campaign Paused',
            className: 'bg-gray-400 text-gray-800'
        },
        ADSET_PAUSED: {
            label: 'Adset Paused',
            className: 'bg-gray-400 text-gray-800'
        },
        UNKNOWN: {
            label: 'Unknown',
            className: 'bg-slate-400 text-white'
        }
    };
    const status = getCampaignStatusBadge(
        campaignDetails?.campaign?.effective_status,
        campaignDetails?.campaign?.endTime,
        statusMap
    );
    const isFinishedCampaign = status.label === 'Finished';
    const currency = campaignDetails?.campaign?.currency || campaignDetails?.accountInfo?.currency || ''
    const timezone = campaignDetails?.campaign?.timezone || campaignDetails?.accountInfo?.timezone_name || ''
    const insightsAvailable = isMetaInsightsAvailable(campaignDetails?.insights);
    const formatDateToShort = (dateString) => formatCampaignDateTime(dateString, timezone, { dateOnly: true });
    const DataArray = [
        {
            key: 'Total Spent',
            value: formatInsightCurrency(campaignDetails?.insights?.spend, insightsAvailable, currency),
            icon: TotalSpentIcon,
        },
        {
            key: 'Reach',
            value: formatInsightMetric(campaignDetails?.insights?.reach, insightsAvailable),
            icon: ReachIcon,
        },
        {
            key: 'Likes & Reactions',
            value: formatInsightMetric(campaignDetails?.actions?.post_reaction ?? 0, insightsAvailable),
            icon: LikesReactionsIcon,
        },
        {
            key: 'Ad Link clicks',
            value: formatInsightMetric(campaignDetails?.actions?.link_click ?? 0, insightsAvailable),
            icon: AdLinkClicksIcon,
        },
        {
            key: 'Ad Comments',
            value: formatInsightMetric(campaignDetails?.actions?.comment ?? 0, insightsAvailable),
            icon: AdCommentsIcon,
        },
        {
            key: 'Ad Shares',
            value: formatInsightMetric(0, insightsAvailable),
            icon: AdSharesIcon,
        },
        {
            key: 'Impressions',
            value: formatInsightMetric(campaignDetails?.insights?.impressions, insightsAvailable),
            icon: ImpressionsIcon,
        },
        {
            key: 'CPM',
            value: formatInsightCurrency(campaignDetails?.insights?.cpm, insightsAvailable, currency),
            icon: CpmIcon,
        },
    ]
    const spentAmount = insightsAvailable ? Number(campaignDetails?.insights?.spend || 0) : 0;
    const budgetPercentage = Math.min((Number(spentAmount).toFixed(2) / Number(campaignDetails?.campaign?.totalBudget || 0).toFixed(2)) * 100, 100).toFixed(2);

    let timeProgress = 0;
    if (campaignDetails?.campaign?.start_time && campaignDetails?.campaign?.endTime) {
        const startDate = new Date(campaignDetails?.campaign?.start_time);
        const endDate = new Date(campaignDetails?.campaign?.endTime);
        const now = new Date();
        // Total campaign duration
        const totalDuration = endDate - startDate;
        // Duration passed till now
        const elapsed = Math.max(0, Math.min(now - startDate, totalDuration));
        // Percentage progress (time-based)
        timeProgress = ((elapsed / totalDuration) * 100).toFixed(2);
    }

    const getDemographicProgress = (total, value) => {
        return Math.min((Number(value || 0).toFixed(2) / Number(total || 1).toFixed(2)) * 100, 100).toFixed(2) || 0;
    }

    const ageGenderBreakdown = (campaignDetails?.ageGenderBreakdownData || [])
        .map(item => ({
            ...item,
            clicks: Number(item.clicks) || 0
        }))
        .sort((a, b) => b.clicks - a.clicks) // highest clicks first
        .slice(0, 5); // only top 5

    // default placeholders for age gender breakdown if no data comes
    const noGenderBreakDown = [
        { age: "18-24", gender: "female", clicks: 0 },
        { age: "18-24", gender: "male", clicks: 0 },
        { age: "25-34", gender: "female", clicks: 0 },
        { age: "25-34", gender: "male", clicks: 0 },
        { age: "35-44", gender: "female", clicks: 0 },
    ];
    // if no data, create 5 empty placeholders for age gender breakdown
    const topAgeGenderBreakdown = ageGenderBreakdown.length > 0
        ? ageGenderBreakdown
        : noGenderBreakDown;

    const geoBreakdown = (campaignDetails?.geoBreakdown || [])
        .map(item => ({
            ...item,
            clicks: Number(item.clicks) || 0
        }))
        .sort((a, b) => b.clicks - a.clicks) // highest first

    // If no data, show placeholder countries with 0 clicks
    const noGeoBreakdown = [
        { country: "IN", clicks: 0 },
        { country: "US", clicks: 0 },
        { country: "GB", clicks: 0 },
        { country: "CA", clicks: 0 },
        { country: "AU", clicks: 0 },
    ];

    const topGeoBreakdown = geoBreakdown.length > 0 ? geoBreakdown.slice(0, 5) : noGeoBreakdown;
    const getCountryName = (code) => {
        try {
            const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
            return regionNames.of(code.toUpperCase()) || code;
        } catch {
            return code; // fallback if Intl not supported
        }
    };

    // Utility to sum clicks by filter
    const platformClicksData = campaignDetails?.platformClicksData || [];

    const getClicks = (filters) => {
        return platformClicksData
            ?.filter(item =>
                filters.some(
                    f =>
                        item.publisherPlatform === f.publisherPlatform &&
                        item.position === f.position
                )
            )
            ?.reduce((sum, item) => sum + Number(item.clicks || 0), 0);
    };

    const reelsClicks = getClicks([
        { publisherPlatform: "facebook", position: "facebook_reels" },
        { publisherPlatform: "instagram", position: "instagram_reels" },
    ]);

    const storiesClicks = getClicks([
        { publisherPlatform: "facebook", position: "facebook_stories" },
        { publisherPlatform: "instagram", position: "instagram_stories" },
    ]);

    const igPostsClicks = getClicks([
        { publisherPlatform: "instagram", position: "feed" },
        { publisherPlatform: "instagram", position: "instagram_explore" },
    ]);

    const openEditModal = (value) => {
        if (isFinishedCampaign) return;
        setOpenFor(value);
        setShowBudgetModal(true);
    }

    const duplicatedModal = () => {
        const duplicatedCamapignDetails = {
            goal: goalMatch?.name || '',
            startDate: '',
            endDate: '',
            budget: campaignDetails?.campaign?.totalBudget,
            budgetType: campaignDetails?.getAdSets[0]?.budgetType,
            currency: campaignDetails?.campaign?.currency || campaignDetails?.accountInfo?.currency,
            timezone: campaignDetails?.campaign?.timezone || campaignDetails?.accountInfo?.timezone_name || '',
            audience: campaignDetails?.getAdSets[0]?.targeting?.interests || [],
            language: campaignDetails?.getAdSets[0]?.targeting?.languages || [],
            locations: campaignDetails?.getAdSets.reduce((acc, adSet) => {
                acc.countries.push(...(adSet?.targeting?.location?.countries || []));
                acc.regions.push(...(adSet?.targeting?.location?.regions || []));
                acc.cities.push(...(adSet?.targeting?.location?.cities || []));
                return acc;
            }, { countries: [], regions: [], cities: [] }),
            gender: campaignDetails?.getAdSets[0]?.targeting?.gender || 'All',
            ageRange: {
                min: campaignDetails?.getAdSets[0]?.targeting?.ageRange?.min || 18,
                max: campaignDetails?.getAdSets[0]?.targeting?.ageRange?.max || 65
            },
            interests: campaignDetails?.getAdSets[0]?.targeting?.interests || [],
            creatives: campaignDetails?.creativeDetails,
            campaignId: campaignDetails?.campaign?.id,
            adAccountId: campaignDetails?.accountInfo?.id,
            instaInfo: campaignDetails?.campaign?.instagramInfo || {},
            pageInfo: campaignDetails?.campaign?.pageInfo || {}
        }
        dispatch(actions.updateDuplicateCampaign(duplicatedCamapignDetails));
        setShowDuplicateModal(true)
    }

    const handleSend = (input) => {
        //BLOCK if no active subscription
        if (!hasActiveSubscription) {
            showToast("info", "Please subscribe to continue using Amplitude AI.");
            setShowPricingPlanModal(true);   // Open subscription modal
            return;
        }
        //BLOCK if no credits
        if (!creditDetails?.data || creditDetails?.data?.remaining <= 0) {
            showToast("info", "You are out of credits. Please purchase more to continue.");
            setShowCreditPlanModal(true); // Open credit purchase modal
            return;
        }
        dispatch(sendMessage(toast, input));
        navigate("/admin/amplitude-ai");
    };
    return (
        <div className='CampaignEditor-page py-5'>
            <div className="container max-w-full">
                <div className="card bg-gradient-to-r from-theme_clr1 to-theme_clr2 rounded-xl mb-4 p-4 lg:p-5">
                    <div className='flex flex-wrap items-center justify-between gap-[11px]'>
                        <h5 className='flex items-center text-white text-[23px] font-manrope font-bold leading-tight'>Campaign Editor <i className="bi bi-bar-chart-line-fill text-4xl leading-none ms-8"></i></h5>
                        <button className='btn-custom2 w-[192px] flex items-center justify-center gap-5 shadow-lg' onClick={() => navigate('/admin/growth-engine/campaign')}>New Campaign <i className='bi bi-plus-circle text-[20px]'></i></button>
                    </div>
                </div>

                <div className="info-wrapper bg-theme_clr1 rounded-lg p-4">
                    {/* Card Info */}
                    <div className="item-info flex flex-col sm:flex-row sm:flex-wrap gap-4 flex-1 bg-white rounded-md mb-4 p-3">
                        {loading ? (
                            <div className="w-[110px] h-[110px] bg-gray-200 rounded-2xl animate-pulse"></div>
                        ) : (
                            <CampaignThumbnail
                                campaign={campaignDetails?.campaign}
                                goalMatch={goalMatch}
                                className="w-[110px] h-[110px] rounded-2xl"
                                iconClassName="w-full h-full object-cover"
                            />
                        )}
                        <div className="info-wrapper flex-1 flex flex-col lg:flex-row lg:flex-wrap lg:items-center justify-between gap-x-8 gap-y-3">
                            <div className="content-wrapper">
                                {/* Title */}
                                {loading ? (
                                    <div className="h-6 w-40 bg-gray-200 rounded-md animate-pulse mb-2"></div>
                                ) : (
                                    <div className="text-theme_clr1 text-[20px] font-medium mb-1">
                                        {getCampaignTargetLabel(campaignDetails?.campaign)}
                                    </div>
                                )}

                                {/* Description */}
                                {loading ? (
                                    <div className="h-4 w-32 bg-gray-200 rounded-md animate-pulse mb-3"></div>
                                ) : (
                                    <div className="description text-theme_clr1 font-inter mb-3">
                                        {campaignDetails?.campaign?.targetObject?.targetType === 'youtubeContent'
                                            ? (campaignDetails.campaign.targetObject.channelTitle
                                                || campaignDetails.campaign.targetObject.artistName
                                                || artistInfo?.name)
                                            : (campaignDetails?.campaign?.targetObject?.artistName || artistInfo?.name)}
                                    </div>
                                )}

                                {/* Buttons */}
                                <div className="btn-wrapper flex flex-wrap items-center gap-x-4 gap-y-3">
                                    {loading ? (
                                        <>
                                            <div className="h-8 w-28 bg-gray-200 rounded-md animate-pulse"></div>
                                            <div className="h-8 w-20 bg-gray-200 rounded-md animate-pulse"></div>
                                        </>
                                    ) : (
                                        <>
                                            <button className="bg-theme_clr1 text-white text-[14px] font-inter font-medium flex items-center gap-1 rounded-[4px] py-2 px-4">
                                                {goalMatch ? (
                                                    <ObjectiveIcon
                                                        icon={goalMatch.icon}
                                                        name={goalMatch.name}
                                                        variant="white"
                                                        className="w-4 h-4"
                                                    />
                                                ) : null}
                                                {objectiveLabel}
                                            </button>
                                            <button
                                                className={`${status.className} text-[14px] min-w-[80px] font-inter font-medium flex items-center justify-center gap-1 rounded-3xl uppercase py-2 px-4`}
                                            >
                                                {status.label}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="actions">
                                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                    <button className="text-theme_clr1 font-semibold flex items-center gap-2" onClick={() => duplicatedModal()}><i className="bi bi-copy"></i> Duplicate</button>
                                    <button
                                        className={`font-semibold flex items-center gap-2 ${isFinishedCampaign ? "cursor-not-allowed text-slate-400" : "text-theme_clr1"}`}
                                        onClick={() => openEditModal('name')}
                                        disabled={isFinishedCampaign}
                                        title={isFinishedCampaign ? "Finished campaigns cannot be renamed" : undefined}
                                    >
                                        <i className="bi bi-pencil"></i> Rename
                                    </button>
                                    <button
                                        className={`font-semibold flex items-center gap-2 ${isFinishedCampaign ? "cursor-not-allowed text-slate-400" : "text-theme_clr1"}`}
                                        onClick={() => openEditModal('status')}
                                        disabled={isFinishedCampaign}
                                        title={isFinishedCampaign ? "Finished campaigns cannot be activated or paused" : undefined}
                                    >
                                        {campaignDetails?.campaign?.effective_status === 'PAUSED' ? (
                                            <>
                                                <i className="bi bi-play-fill"></i>
                                                <span>Activate</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-pause-fill"></i>
                                                <span>Pause</span>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        className={`font-semibold flex items-center gap-2 ${isFinishedCampaign ? "cursor-not-allowed text-slate-400" : "text-theme_clr1"}`}
                                        onClick={() => openEditModal('budget')}
                                        disabled={isFinishedCampaign}
                                        title={isFinishedCampaign ? "Finished campaigns cannot edit budget" : undefined}
                                    >
                                        <i className="bi bi-cash-coin"></i> Edit Budget
                                    </button>
                                    <button
                                        className={`font-semibold flex items-center gap-2 ${isFinishedCampaign ? "cursor-not-allowed text-slate-400" : "text-theme_clr1"}`}
                                        onClick={() => openEditModal('endDate')}
                                        disabled={isFinishedCampaign}
                                        title={isFinishedCampaign ? "Finished campaigns cannot edit end date" : undefined}
                                    >
                                        <i className="bi bi-calendar4-week"></i> Edit End Date
                                    </button>
                                    {showBudgetModal && !isFinishedCampaign && (
                                        <EditCampaignModal
                                            showEditBudgetModal={showBudgetModal}
                                            closeEditBudgetModal={() => setShowBudgetModal(false)}
                                            initialBudget={campaignDetails?.campaign?.totalBudget}
                                            startDate={campaignDetails?.campaign?.start_time}
                                            initialEndDate={campaignDetails?.campaign?.stop_time || null}
                                            currency={campaignDetails?.accountInfo?.currency}
                                            budgetType={campaignDetails?.getAdSets[0]?.budgetType || campaignDetails?.campaign?.budgetType}
                                            campaignId={campaignDetails?.campaign?.id}
                                            adAccountId={campaignDetails?.accountInfo?.id}
                                            openFor={openFor}
                                            name={campaignDetails?.campaign?.name}
                                            status={campaignDetails?.campaign?.effective_status}
                                            timezone={timezone}
                                            spentAmount={spentAmount}
                                        />
                                    )}
                                    {showDuplicateModal && (
                                        <DuplicateCampaignModal
                                            show={showDuplicateModal}
                                            close={() => setShowDuplicateModal(false)}
                                            initialBudget={campaignDetails?.campaign?.totalBudget}
                                            currency={campaignDetails?.accountInfo?.currency}
                                        />
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Grid Section */}
                    <div className="grid grid-cols-1 xl:grid-cols-[1fr_350px] gap-4">
                        <div className="left-sec">
                            {status.label === 'Finished' && (
                            <div className="bg-green-50 rounded-lg mb-4 p-4">
                                <div className="text-black text-lg font-semibold mb-1">Your campaign has finished!</div>
                                <div className="text-[#6B7280] text-base">Your analytics are updated with final stats. Please reach out to our team if you have any questions or need assistance on next steps.</div>
                            </div>
                            )}
                            <div className="bg-white rounded-lg p-4">
                                <div className='wrapper flex flex-wrap mb-4'>
                                    <ChatInput
                                        title={`Launch campaigns to grow your fanbase, ${displayName}.`}
                                        inputPlaceholder={'Use Amplitude in crafting the perfect campaign based on your vision.'}
                                        onSend={handleSend}
                                        disabled={showLoad}
                                    />
                                </div>
                                <div className="flex flex-col lg:flex-row justify-between gap-3 mb-4">
                                    <div className="info-card flex-1 border rounded-lg p-4">
                                        <div className="mb-2">
                                            <div className="text-black text-lg font-semibold">Budget</div>
                                            <div className="text-[#6B7280] text-sm">{currency}{campaignDetails?.campaign?.totalBudget?.toFixed(2)}</div>
                                        </div>
                                        <div className='relative w-100 h-2 bg-gray-200 overflow-hidden rounded-xl mb-2'>
                                            <div className='h-full bg-theme_clr2' style={{ width: `${budgetPercentage}%` }} />
                                        </div>
                                        <div className="flex items-center gap-1"><span className="text-[#6B7280] text-base">Spent: </span>{formatInsightCurrency(campaignDetails?.insights?.spend, insightsAvailable, currency, 2)}</div>
                                    </div>
                                    <div className="info-card flex-1 border rounded-lg p-4">
                                        <div className="mb-2">
                                            <div className="text-black text-lg font-semibold">Length</div>
                                            <div className="text-[#6B7280] text-sm">{campaignDetails?.campaign?.endTime ? `Ended on ${formatDateToShort(campaignDetails?.campaign?.endTime)}` : ''}</div>
                                        </div>
                                        <div className='relative w-100 h-2 bg-gray-200 overflow-hidden rounded-xl mb-2'>
                                            <div className='h-full bg-theme_clr2' style={{ width: `${timeProgress}%` }} />
                                        </div>
                                        <div className="text-sm flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div>
                                                    <span className="text-[#6B7280]">Start:</span>{" "}
                                                    {campaignDetails?.campaign?.start_time
                                                        ? formatCampaignDateTime(campaignDetails.campaign.start_time, timezone, { dateOnly: true })
                                                        : "-"}
                                                </div>
                                                {campaignDetails?.campaign?.start_time ? (
                                                    <div className="whitespace-nowrap">
                                                        {formatCampaignDateTime(campaignDetails.campaign.start_time, timezone, { timeOnly: true })}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className="min-w-0 text-right">
                                                <div>
                                                    <span className="text-[#6B7280]">End:</span>{" "}
                                                    {campaignDetails?.campaign?.endTime
                                                        ? formatCampaignDateTime(campaignDetails.campaign.endTime, timezone, { dateOnly: true })
                                                        : "-"}
                                                </div>
                                                {campaignDetails?.campaign?.endTime ? (
                                                    <div className="whitespace-nowrap">
                                                        {formatCampaignDateTime(campaignDetails.campaign.endTime, timezone, { timeOnly: true })}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        {timezone ? (
                                            <div className="text-xs text-[#6B7280] mt-1">
                                                Timezone: {getTimezoneLabel(timezone)}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>

                                <div className="performance-section grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 mb-4">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className="text-black text-lg font-semibold">Performance</div>
                                        <button className='bg-theme_clr1 text-white text-[14px] font-inter font-medium flex items-center gap-1 tracking-[0.037] rounded-[4px] py-2 px-4'><i className="bi bi-shuffle text-theme_clr4 text-sm me-1"></i> Conversions Enabled</button>
                                    </div>
                                    <div className="text-sm">
                                        <div className="grid grid-cols-[auto_1fr] gap-x-1.5 gap-y-0.5 mb-1">
                                            <span className="text-[#6B7280]">Goal:</span>
                                            <span className="text-black">{conversionSettings.goal}</span>
                                        </div>
                                        {campaignDetails?.campaign?.targetObject?.targetType === "song" &&
                                            (campaignDetails?.campaign?.targetObject?.autoplayTrackSongName
                                                || (campaignDetails?.campaign?.targetObject?.trackType === 'track'
                                                    && campaignDetails?.campaign?.targetObject?.trackName)) && (
                                            <div className="grid grid-cols-[auto_1fr] gap-x-1.5 gap-y-0.5">
                                                <span className="text-[#6B7280]">Autoplay Track:</span>
                                                <span className="text-black">
                                                    {campaignDetails.campaign.targetObject.autoplayTrackSongName
                                                        || campaignDetails.campaign.targetObject.trackName}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="info-card grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[2.5fr_1fr_1fr] gap-x-2 gap-y-3 border rounded-lg mb-4 p-4">
                                    <div className="info-box flex gap-2 sm:col-span-2 lg:col-span-1">
                                        <span className="inline-flex h-[45px] w-[45px] min-w-[45px] items-center justify-center overflow-hidden rounded-[10px]">
                                            <img src={AdClicksIcon} alt="Ad Clicks" className="h-[45px] w-[45px] object-contain" />
                                        </span>
                                        <div className="info-wrapper">
                                            <div className="label text-black text-base font-semibold">Ad Clicks</div>
                                            <div className="value text-sm text-[#6B7280]">
                                                {insightsAvailable
                                                    ? `Your ad received ${campaignDetails?.insights?.clicks || 0} clicks.`
                                                    : 'Ad click data is currently unavailable.'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="info-box">
                                        <div className="label text-sm text-[#6B7280]">Clicks</div>
                                        <div className="value text-black text-lg font-medium">{formatInsightMetric(campaignDetails?.insights?.clicks, insightsAvailable)}</div>
                                    </div>
                                    <div className="info-box">
                                        <div className="label text-sm text-[#6B7280]">Cost-per-Clicks</div>
                                        <div className="value text-black text-lg font-medium">{formatInsightCurrency(campaignDetails?.insights?.cpc, insightsAvailable, currency)}</div>
                                    </div>
                                </div>

                                <div className="info-card grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[2.5fr_1fr_1fr] gap-x-2 gap-y-3 border rounded-lg mb-4 p-4">
                                    <div className="info-box flex gap-2 sm:col-span-2 lg:col-span-1">
                                        <span className="inline-flex h-[45px] w-[45px] min-w-[45px] items-center justify-center overflow-hidden rounded-[10px]">
                                            <img src={ConversionsIcon} alt="Conversions" className="h-[45px] w-[45px] object-contain" />
                                        </span>

                                        <div className="info-wrapper">
                                            <div className="label text-black text-base font-semibold">{conversionSettings.conversionTitle}</div>
                                            <div className="value text-sm text-[#6B7280]">
                                                {conversionMetrics.conversionsAvailable
                                                    ? `${conversionMetrics.conversions} ${conversionSettings.conversionDescription}`
                                                    : 'Conversion data is currently unavailable.'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="info-box">
                                        <div className="label text-sm text-[#6B7280]">{conversionSettings.conversionCountLabel}</div>
                                        <div className="value text-black text-lg font-medium">
                                            {formatConversionMetric(conversionMetrics.conversions, conversionMetrics.conversionsAvailable)}
                                        </div>
                                    </div>
                                    <div className="info-box">
                                        <div className="label text-sm text-[#6B7280]">{conversionSettings.conversionCostLabel}</div>
                                        <div className="value text-black text-lg font-medium">
                                            {formatCostPerConversion(
                                                conversionMetrics.costPerConversion,
                                                conversionMetrics.conversionsAvailable && conversionMetrics.costPerConversion !== null,
                                                currency
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid-wrapper grid grid-cols-[1fr_1fr] lg:grid-cols-[1fr_1fr_1fr] xl:grid-cols-[1fr_1fr_1fr_1fr] gap-3 mb-4">
                                    {DataArray.map((data, index) => (
                                        <div className="grid-item border rounded-lg p-3 sm:p-4" key={index}>
                                            <span className="mb-2 inline-flex h-[45px] w-[45px] min-w-[45px] items-center justify-center overflow-hidden rounded-[10px]">
                                                <img src={data.icon} alt={data.key} className="h-[45px] w-[45px] object-contain" />
                                            </span>
                                            <div className="value text-sm text-[#6B7280] mb-1">{data.key}</div>
                                            <div className="label text-black text-base font-semibold">{data.value}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="grid-wrapper grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 mb-4">
                                    <div className="info-card bg-theme_clr1 rounded-lg p-4">
                                        <div className="flex flex-wrap items-center justify-between gap-2 font-medium mb-3">
                                            <span className="text-white">Primary Market</span>
                                            <span className="text-theme_clr2"
                                                onClick={openEditGeographyModal}
                                            ><i className='bi bi-pencil-fill me-1'></i>{campaignDetails?.getAdSets[0]?.budgetPercentage || 100}% of budget</span>
                                        </div>
                                        {campaignDetails?.getAdSets?.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    ...(campaignDetails?.getAdSets[0]?.targeting?.location?.countries || []),
                                                    ...(campaignDetails?.getAdSets[0]?.targeting?.location?.regions || []),
                                                    ...(campaignDetails?.getAdSets[0]?.targeting?.location?.cities || []),
                                                ].map((loc, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="badge border text-theme_clr2 border-theme_clr2 rounded-3xl capitalize px-3 py-1"
                                                    >
                                                        {loc.name || loc.id}
                                                    </span>
                                                ))}
                                            </div>
                                        )}


                                    </div>
                                    <div className="info-card bg-theme_clr1 rounded-lg p-4">
                                        <div className="flex flex-wrap items-center justify-between gap-2 font-medium mb-3">
                                            <span className="text-white">Secondary Markets</span>
                                            <span className="text-theme_clr2"
                                                onClick={openEditGeographyModal}
                                            ><i className='bi bi-pencil-fill me-1'></i> {campaignDetails?.getAdSets[1]?.budgetPercentage || 0}% of budget</span>
                                        </div>

                                        {campaignDetails?.getAdSets?.[1] ? (
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    ...(campaignDetails.getAdSets[1]?.targeting?.location?.countries || []),
                                                    ...(campaignDetails.getAdSets[1]?.targeting?.location?.regions || []),
                                                    ...(campaignDetails.getAdSets[1]?.targeting?.location?.cities || []),
                                                ].map((loc, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="badge border text-theme_clr2 border-theme_clr2 rounded-3xl capitalize px-3 py-1"
                                                    >
                                                        {loc.name || loc.id}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flex justify-center items-center w-full py-6">
                                                <span className="text-gray-400 italic">No location in secondary market</span>
                                            </div>

                                        )}
                                    </div>
                                    {/* Edit Geography Modal */}
                                    {showEditGeographyModal && (
                                        <EditGeographyModal
                                            campaignId={campaignDetails?.campaign?.id}
                                            adAccountId={campaignDetails?.accountInfo?.id}
                                            getAdSets={campaignDetails?.getAdSets || []}
                                            showEditGeographyModal={showEditGeographyModal}
                                            closeEditGeographyModal={closeEditGeographyModal}
                                            budgetType={campaignDetails?.campaign?.budgetType}      // pass budgetType
                                            startTime={campaignDetails?.campaign?.start_time}        // pass startTime
                                            endTime={campaignDetails?.campaign?.stop_time}
                                        />
                                    )}
                                </div>

                                {showGatedFeatureFmBreakdown && (
                                    <>
                                        <div className="info-wrapper">
                                            <div className="text-black text-lg font-semibold mb-1">{featureFmBreakdownCopy.title}</div>
                                            <div className="text-[#6B7280] text-base">{featureFmBreakdownCopy.description}</div>
                                        </div>
                                        <hr className='my-4' />
                                        <div className="relative overflow-hidden rounded-lg min-h-[220px]">
                                            <div className="info-wrapper space-y-5 px-4 py-2 pointer-events-none select-none blur-[2px] opacity-80" aria-hidden="true">
                                                {gatedDestinationPreview.kind === 'event' ? (
                                                    <>
                                                        <div className="space-y-3">
                                                            <div className="text-black text-base font-semibold">Ticket Clicks</div>
                                                            {gatedDestinationPreview.tickets.map((destination, index) => {
                                                                const { icon, label } = getFeatureFmDestinationDisplay(destination);
                                                                return (
                                                                    <div key={`gated-ticket-${destination.key}-${index}`} className="flex items-center justify-between gap-6">
                                                                        <img src={icon} alt="" className="w-8 h-8 object-contain" />
                                                                        <span className="min-w-[110px] text-sm font-medium text-black">{label}</span>
                                                                        <div className="w-full bg-gray-200 rounded-md relative">
                                                                            <div
                                                                                className="bg-theme_clr2 h-5 text-sm font-medium text-body text-center leading-none rounded-md text-nowrap py-1"
                                                                                style={{ width: `${destination.percent}%` }}
                                                                            ></div>
                                                                            <div className="value text-sm font-medium text-body leading-none absolute top-1/2 -translate-y-1/2 end-4">
                                                                                {destination.clicks}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        <div className="space-y-3">
                                                            <div className="text-black text-base font-semibold">Engagement Clicks</div>
                                                            <div className="text-[#6B7280] text-sm">Tracked on the tour page, but excluded from conversions and Cost per Conversion.</div>
                                                            {gatedDestinationPreview.engagement.map((destination, index) => {
                                                                const { icon, label } = getFeatureFmDestinationDisplay(destination);
                                                                return (
                                                                    <div key={`gated-engagement-${destination.key}-${index}`} className="flex items-center justify-between gap-6">
                                                                        <img src={icon} alt="" className="w-8 h-8 object-contain" />
                                                                        <span className="min-w-[110px] text-sm font-medium text-black">{label}</span>
                                                                        <div className="w-full bg-gray-200 rounded-md relative">
                                                                            <div
                                                                                className="bg-gray-400 h-5 text-sm font-medium text-body text-center leading-none rounded-md text-nowrap py-1"
                                                                                style={{ width: `${destination.percent}%` }}
                                                                            ></div>
                                                                            <div className="value text-sm font-medium text-body leading-none absolute top-1/2 -translate-y-1/2 end-4">
                                                                                {destination.clicks}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </>
                                                ) : (
                                                    gatedDestinationPreview.destinations.map((destination, index) => {
                                                        const { icon, label } = getFeatureFmDestinationDisplay(destination);
                                                        return (
                                                            <div key={`gated-${destination.key}-${index}`} className="flex items-center justify-between gap-6">
                                                                <img src={icon} alt="" className="w-8 h-8 object-contain" />
                                                                <span className="min-w-[110px] text-sm font-medium text-black">{label}</span>
                                                                <div className="w-full bg-gray-200 rounded-md relative">
                                                                    <div
                                                                        className="bg-theme_clr2 h-5 text-sm font-medium text-body text-center leading-none rounded-md text-nowrap py-1"
                                                                        style={{ width: `${destination.percent}%` }}
                                                                    ></div>
                                                                    <div className="value text-sm font-medium text-body leading-none absolute top-1/2 -translate-y-1/2 end-4">
                                                                        {destination.clicks}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                            <ArtistManagerDspLockOverlay
                                                onSubscribe={() => setShowPricingPlanModal(true)}
                                                title="Unlock detailed destination insights"
                                                description="Destination breakdown is available on Pro. Conversion counts for this campaign use Meta destination link clicks."
                                                buttonLabel="Go Pro"
                                            />
                                        </div>
                                    </>
                                )}

                                {showFeatureFmBreakdown && (
                                    <>
                                        <div className="info-wrapper">
                                            <div className="text-black text-lg font-semibold mb-1">{featureFmBreakdownCopy.title}</div>
                                            <div className="text-[#6B7280] text-base">{featureFmBreakdownCopy.description}</div>
                                        </div>

                                        <hr className='my-4' />
                                        <div className="info-wrapper space-y-5">
                                            {isEventBreakdown ? (
                                                <>
                                                    {ticketDestinations.length > 0 && (
                                                        <div className="space-y-3">
                                                            <div className="text-black text-base font-semibold">Ticket Clicks</div>
                                                            {ticketDestinations.map((destination, index) => {
                                                                const { icon, label } = getFeatureFmDestinationDisplay(destination);
                                                                return (
                                                                    <div key={`ticket-${destination.key}-${index}`} className="flex items-center justify-between gap-6">
                                                                        <img src={icon} alt={label} className="w-8 h-8 object-contain" />
                                                                        <span className="min-w-[110px] text-sm font-medium text-black">{label}</span>
                                                                        <div className="w-full bg-gray-200 rounded-md relative">
                                                                            <div
                                                                                className="bg-theme_clr2 h-5 text-sm font-medium text-body text-center leading-none rounded-md text-nowrap py-1"
                                                                                style={{ width: `${destination.percent}%` }}
                                                                            ></div>
                                                                            <div className="value text-sm font-medium text-body leading-none absolute top-1/2 -translate-y-1/2 end-4">
                                                                                {destination.clicks}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {engagementDestinations.length > 0 && (
                                                        <div className="space-y-3">
                                                            <div className="text-black text-base font-semibold">Engagement Clicks</div>
                                                            <div className="text-[#6B7280] text-sm">Tracked on the tour page, but excluded from conversions and Cost per Conversion.</div>
                                                            {engagementDestinations.map((destination, index) => {
                                                                const { icon, label } = getFeatureFmDestinationDisplay(destination);
                                                                return (
                                                                    <div key={`engagement-${destination.key}-${index}`} className="flex items-center justify-between gap-6">
                                                                        <img src={icon} alt={label} className="w-8 h-8 object-contain" />
                                                                        <span className="min-w-[110px] text-sm font-medium text-black">{label}</span>
                                                                        <div className="w-full bg-gray-200 rounded-md relative">
                                                                            <div
                                                                                className="bg-gray-400 h-5 text-sm font-medium text-body text-center leading-none rounded-md text-nowrap py-1"
                                                                                style={{ width: `${destination.percent}%` }}
                                                                            ></div>
                                                                            <div className="value text-sm font-medium text-body leading-none absolute top-1/2 -translate-y-1/2 end-4">
                                                                                {destination.clicks}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </>
                                            ) : featureFmDestinations.length > 0 ? (
                                                featureFmDestinations.map((destination, index) => {
                                                    const { icon, label } = getFeatureFmDestinationDisplay(destination);

                                                    return (
                                                        <div key={`${destination.key}-${index}`} className="flex items-center justify-between gap-6">
                                                            <img src={icon} alt={label} className="w-8 h-8 object-contain" />
                                                            <span className="min-w-[110px] text-sm font-medium text-black">{label}</span>
                                                            <div className="w-full bg-gray-200 rounded-md relative">
                                                                <div
                                                                    className="bg-theme_clr2 h-5 text-sm font-medium text-body text-center leading-none rounded-md text-nowrap py-1"
                                                                    style={{ width: `${destination.percent}%` }}
                                                                ></div>
                                                                <div className="value text-sm font-medium text-body leading-none absolute top-1/2 -translate-y-1/2 end-4">
                                                                    {destination.clicks}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="text-[#6B7280] text-sm">
                                                    {conversionSettings.conversionEvent === CONVERSION_EVENTS.PRE_SAVE
                                                        ? 'No successful pre-saves yet.'
                                                        : 'No destination clicks yet.'}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                <hr className='my-4' />
                                <div className="grid-wrapper grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8">
                                    <div className="left-section">
                                        <div className="mb-4">
                                            <div className="text-black text-lg font-semibold">Top Demographics by Clicks</div>
                                            <div className="text-[#6B7280] text-base">
                                                {
                                                    ageGenderBreakdown && ageGenderBreakdown.length > 0 > 0 ?
                                                        `Ads were most successful among ${topAgeGenderBreakdown[0].gender}, Ages ${topAgeGenderBreakdown[0].age}.`
                                                        : ' No data available'
                                                }
                                            </div>
                                        </div>
                                        <div className="progressbar-wrapper flex flex-col gap-3">
                                            {topAgeGenderBreakdown.map((item, index) => {
                                                const maxClicks = campaignDetails?.insights?.clicks || 1;
                                                const percentage = getDemographicProgress(maxClicks, item?.clicks)
                                                return (
                                                    <div
                                                        key={item.age + item.gender + index}
                                                        className="progress-info flex flex-col sm:flex-row sm:items-center sm:gap-2"
                                                    >
                                                        <div className="sm:w-[80px] text-sm font-medium capitalize mb-1">
                                                            {item.age} {item.gender}
                                                        </div>
                                                        <div className="w-full bg-gray-200 rounded-md relative">
                                                            <div
                                                                className="bg-theme_clr2 h-8 rounded-md text-nowrap py-1"
                                                                style={{ width: `${percentage}%` }}
                                                            ></div>
                                                            <div className="value text-sm font-medium text-body leading-none absolute top-1/2 -translate-y-1/2 end-4">
                                                                {item.clicks} Clicks
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                            }
                                        </div>
                                    </div>
                                    <div className="right-section">
                                        <div className="mb-4">
                                            <div className="text-black text-lg font-semibold">Top Country by Clicks</div>
                                            <div className="text-[#6B7280] text-base">
                                                {geoBreakdown.length > 0
                                                    ? `${getCountryName(geoBreakdown[0].country)} and ${getCountryName(geoBreakdown[1].country)} were the key ad click sources.`
                                                    : "No country data available"}
                                            </div>
                                        </div>
                                        <div className="progressbar-wrapper flex flex-col gap-3">
                                            {topGeoBreakdown.map((item, idx) => {
                                                const maxClicks = campaignDetails?.insights?.clicks || 1;
                                                const percentage = getDemographicProgress(maxClicks, item?.clicks)
                                                return (
                                                    <div
                                                        key={idx}
                                                        className="progress-info flex items-center justify-between gap-4 md:gap-5 mt-[6px]"
                                                    >
                                                        {/* Flag */}
                                                        <div className="text-2xl font-medium">
                                                            <span className={`fi fi-${item.country.toLowerCase()}`}></span>
                                                        </div>

                                                        {/* Progress Bar */}
                                                        <div className="w-full bg-gray-200 rounded-md relative">
                                                            <div
                                                                className="bg-theme_clr2 h-8 rounded-md text-nowrap py-1"
                                                                style={{ width: `${percentage}%` }}
                                                            ></div>
                                                            <div className="value text-sm font-medium text-body leading-none absolute top-1/2 -translate-y-1/2 end-4">
                                                                {item.clicks} Clicks
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="right-sec">
                            <div className="bg-white rounded-lg mb-4 p-4">
                                <div className="text-black text-lg font-semibold mb-1">Creative Performance</div>
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                    <div className="text-gray-500">By Clicks</div>
                                    <label htmlFor="uploadNewAssets" className="text-sm font-medium text-white bg-theme_clr4 rounded-3xl px-5 py-2"> Upload new assets
                                        <input type="file" accept='image/*' id="uploadNewAssets" className="hidden" />
                                    </label>
                                </div>
                                <div className="flex flex-wrap justify-between gap-2">
                                    <div className="info-box text-sm text-center">
                                        <div className="value font-semibold">{reelsClicks}</div>
                                        <div className="label text-gray-500 capitalize">Reels</div>
                                    </div>
                                    <div className="info-box text-sm text-center">
                                        <div className="value font-semibold">{storiesClicks}</div>
                                        <div className="label text-gray-500 capitalize">Stories</div>
                                    </div>
                                    <div className="info-box text-sm text-center">
                                        <div className="value font-semibold">{igPostsClicks}</div>
                                        <div className="label text-gray-500 capitalize">IG Posts</div>
                                    </div>
                                </div>
                            </div>
                            {
                                loading ? (
                                    <div className="bg-white w-full rounded-lg animate-pulse flex items-center justify-center text-theme_clr1 h-[420px] mb-4">
                                        <p>Loading creatives...</p>
                                    </div>
                                )
                                    :
                                    creatives.length > 0 ?
                                        (
                                            <div className="bg-white rounded-lg mb-4 p-4">
                                                <div className="text-black text-lg font-semibold mb-3">Creative Preview</div>
                                                {creatives.length > 1 ? (
                                                    <div className="flex items-center justify-between gap-2 mb-3">
                                                        <button
                                                            type="button"
                                                            className="text-theme_clr1 text-sm font-medium disabled:opacity-40"
                                                            disabled={activeCreativeIndex <= 0}
                                                            onClick={() => setActiveCreativeIndex((prev) => Math.max(prev - 1, 0))}
                                                        >
                                                            <i className="bi bi-chevron-left"></i> Prev
                                                        </button>
                                                        <div className="text-xs text-slate-500">
                                                            Creative {activeCreativeIndex + 1} of {creatives.length}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="text-theme_clr1 text-sm font-medium disabled:opacity-40"
                                                            disabled={activeCreativeIndex >= creatives.length - 1}
                                                            onClick={() => setActiveCreativeIndex((prev) => Math.min(prev + 1, creatives.length - 1))}
                                                        >
                                                            Next <i className="bi bi-chevron-right"></i>
                                                        </button>
                                                    </div>
                                                ) : null}
                                                <MetaAdPreview
                                                    adViewAs={adViewAs}
                                                    onViewChange={setAdViewAs}
                                                    facebookProfile={facebookProfile}
                                                    instagramProfile={instagramProfile}
                                                    adCaption={activeCreative?.adCaption}
                                                    adHeadline={activeCreative?.headline}
                                                    adDescription={activeCreative?.description}
                                                    callToAction={activeCreative?.callToAction}
                                                    destinationUrl={activeCreative?.destinationUrl}
                                                    renderMedia={renderMedia}
                                                    showBrandBanner
                                                />
                                            </div>
                                        )
                                        :
                                        (
                                            <div className="bg-white text-theme_clr1 w-full rounded-lg flex items-center justify-center h-[220px] mb-4 border border-slate-200">
                                                <p className="text-lg font-semibold">No creatives available</p>
                                            </div>
                                        )
                            }

                            <div className="bg-white rounded-lg p-4">
                                <div className="heading-wrapper mb-3">
                                    <div className="text-black text-lg font-semibold">Setup Details</div>
                                    <div className="text-[#6B7280] text-base">Connections used for campaign</div>
                                </div>

                                <div className="cards-wrapper flex flex-col gap-3">
                                    <div className="box border rounded-lg">
                                        <div className="flex items-center gap-3 border-b px-4 py-2">
                                            <span className="bg-gray-200 h-9 w-9 flex items-center justify-center rounded-full">
                                                <i className="bi bi-link text-theme_clr4 text-xl"></i>
                                            </span>
                                            <span className="text-base font-semibold">
                                                {campaignDetails?.campaign?.targetObject?.targetType === 'youtubeContent'
                                                    ? 'YouTube Destination'
                                                    : 'Promoted Link'}
                                            </span>
                                        </div>
                                        <div className="content p-4">
                                            <div className="label text-[#6B7280] text-base mb-1">URL</div>
                                            <div className="description">
                                                {
                                                    campaignDetails?.creativeDetails.length > 0 ?
                                                        <Link to={campaignDetails?.creativeDetails[0]?.destinationUrl || '#'} className="break-words"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {campaignDetails?.creativeDetails[0]?.destinationUrl}</Link>
                                                        : (campaignDetails?.campaign?.targetObject?.youtubeUrl ? (
                                                            <Link to={campaignDetails.campaign.targetObject.youtubeUrl} className="break-words"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {campaignDetails.campaign.targetObject.youtubeUrl}
                                                            </Link>
                                                        ) : '')
                                                }
                                            </div>
                                            {campaignDetails?.campaign?.targetObject?.targetType === 'youtubeContent' ? (
                                                <div className="mt-3 flex items-center gap-3">
                                                    {campaignDetails.campaign.targetObject.targetImage ? (
                                                        <img
                                                            src={campaignDetails.campaign.targetObject.targetImage}
                                                            alt={campaignDetails.campaign.targetObject.targetTitle || 'YouTube'}
                                                            className={campaignDetails.campaign.targetObject.contentType === 'channel'
                                                                ? 'w-10 h-10 rounded-full object-cover'
                                                                : 'w-16 h-12 rounded object-cover'}
                                                        />
                                                    ) : (
                                                        <img src={YouTubeIcon} alt="YouTube" className="w-8 h-8" />
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-semibold text-theme_clr1 truncate">
                                                            {campaignDetails.campaign.targetObject.targetTitle || 'YouTube destination'}
                                                        </div>
                                                        <div className="text-xs text-[#6B7280] capitalize">
                                                            {campaignDetails.campaign.targetObject.contentType === 'video' ? 'YouTube video' : 'YouTube channel'}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="box border rounded-lg">
                                        <div className="flex items-center gap-3 border-b px-4 py-2">
                                            <span className="bg-gray-200 h-9 w-9 flex items-center justify-center rounded-full">
                                                <img src={FacebookIcon} alt="Facebook Icon" className="block" style={{ width: '30px' }} />
                                            </span>
                                            <span className="text-base font-semibold">Campaign</span>
                                        </div>
                                        <div className="content flex flex-col gap-1 px-4 py-3">
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="w-[80px] inline-block text-[#6B7280] text-base me-1">Campaign:</span>
                                                <span>{campaignDetails?.campaign?.name}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="w-[80px] inline-block text-[#6B7280] text-base me-1">ID:</span>
                                                <span>{campaignDetails?.campaign?.campaignId}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="box border rounded-lg">
                                        <div className="flex items-center gap-3 border-b px-4 py-2">
                                            <span className="bg-gray-200 h-9 w-9 flex items-center justify-center rounded-full">
                                                <img src={FacebookIcon} alt="Facebook Icon" className="block" style={{ width: '30px' }} />
                                            </span>
                                            <span className="text-base font-semibold">Add Account <i className="bi bi-box-arrow-up text-theme_clr4 text-lg ms-4"></i></span>
                                        </div>
                                        <div className="content flex flex-col gap-1 px-4 py-3">
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="w-[80px] inline-block text-[#6B7280] text-base me-1">Name:</span>
                                                <span>{campaignDetails?.accountInfo?.name}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="w-[80px] inline-block text-[#6B7280] text-base me-1">ID:</span>
                                                <span>{campaignDetails?.accountInfo?.id}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="w-[80px] inline-block text-[#6B7280] text-base me-1">Currency:</span>
                                                <span>{currency}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="box border rounded-lg">
                                        <div className="flex items-center gap-3 border-b px-4 py-2">
                                            <span className="bg-gray-200 h-9 w-9 flex items-center justify-center rounded-full">
                                                <img src={FacebookIcon} alt="Facebook Icon" className="block" style={{ width: '30px' }} />
                                            </span>
                                            <span className="text-base font-semibold">Facebook Page</span>
                                        </div>
                                        <div className="content flex flex-col gap-1 px-4 py-3">
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="w-[80px] inline-block text-[#6B7280] text-base me-1">Name:</span>
                                                <span>{campaignDetails?.campaign?.pageInfo?.name || ''}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="w-[80px] inline-block text-[#6B7280] text-base me-1">ID:</span>
                                                <span>{campaignDetails?.campaign?.pageInfo?.id || ''}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="box border rounded-lg">
                                        <div className="flex items-center gap-3 border-b px-4 py-2">
                                            <span className="bg-gray-200 h-9 w-9 flex items-center justify-center rounded-full">
                                                <img src={InstagramIcon} alt="Facebook Icon" className="block" style={{ width: '20px' }} />
                                            </span>
                                            <span className="text-base font-semibold">Instagram</span>
                                        </div>
                                        <div className="content flex flex-col gap-1 px-4 py-3">
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="w-[80px] inline-block text-[#6B7280] text-base me-1">Username:</span>
                                                <span>{campaignDetails?.campaign?.instagramInfo?.username || ''}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="w-[80px] inline-block text-[#6B7280] text-base me-1">ID:</span>
                                                <span>{campaignDetails?.campaign?.instagramInfo?.id || ''}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="box border rounded-lg">
                                        <div className="flex items-center gap-3 border-b px-4 py-2">
                                            <span className="bg-gray-200 h-9 w-9 flex items-center justify-center rounded-full">
                                                <i className="bi bi-shuffle text-theme_clr4 text-xl"></i>
                                            </span>
                                            <span className="text-base font-semibold">Conversion Settings</span>
                                        </div>
                                        <div className="content flex flex-col gap-1 px-4 py-3">
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="inline-block text-[#6B7280] text-base me-1">Conversions Enabled</span>
                                                <span>{conversionSettings.conversionsEnabled ? 'Yes' : 'No'}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-x-2">
                                                <span className="inline-block text-[#6B7280] text-base me-1">Conversion Event</span>
                                                <span>{conversionSettings.conversionEvent}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/admin/artist-manager"} />
            <CreditPlanModal isOpen={showCreditPlanModal} onClose={() => setShowCreditPlanModal(false)} />
        </div>
    )
}
